// Deep-link handling for email / push Universal Links (iOS) & App Links (Android).
//
// Notification emails link to https://open.stayyoung.live/<path> — see the
// applinks-host Vercel project and supabase/functions/send-email-notification.
// When the OS opens the app from such a link it fires Capacitor's `appUrlOpen`
// (or exposes it via getLaunchUrl on cold start) with the full external URL.
// We translate that URL into an in-app router path and hand it to navigateTo(),
// the same navigation used by push-notification taps (see navigationRef.ts).

import { normalizeReferralCode } from '@/lib/referralRedemption';

// Hosts whose links belong to this app. Anything else is ignored — we must
// never feed an arbitrary foreign URL into the router.
const APP_LINK_HOSTS = new Set([
  'open.stayyoung.live', // email button base URL (EMAIL_APP_BASE_URL)
  'app.stayyoung.live', // Capacitor WKWebView origin (server.hostname)
  'stayyoung.lovable.app', // web app / browser-fallback origin
]);

/**
 * Extract the in-app router path (pathname + search + hash) from an external
 * link the OS handed us. Returns null when the URL is unparseable or its host
 * isn't one of ours. A bare host resolves to "/" (lands on home).
 */
export function extractInAppPath(rawUrl: string | undefined | null): string | null {
  if (!rawUrl) return null;
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }
  if (!APP_LINK_HOSTS.has(parsed.hostname)) return null;
  const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * What to say in analytics about a link that opened the app: the top-level
 * section only, plus the referral code on an invite link.
 *
 * Deliberately NOT the path. Email links carry ids and can carry tokens in the
 * query, and none of that belongs in an event. The referral code is the one
 * exception — it is the referrer's public share token, and it is what ties an
 * in-app open back to the link that was sent.
 */
export function describeAppLink(path: string): { section: string; code?: string } {
  const segments = path.split(/[?#]/)[0].split('/').filter(Boolean);
  // The router matches case-insensitively, so /Invite/X is the invite page.
  const section = (segments[0] ?? 'home').toLowerCase();
  if (section !== 'invite' || !segments[1]) return { section };
  let code = segments[1];
  try {
    code = decodeURIComponent(code);
  } catch {
    // A malformed escape — report it as it came.
  }
  return { section, code: normalizeReferralCode(code) };
}

/** True if the string contains any ASCII control character (0x00–0x1F or 0x7F). */
function hasControlChar(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * Guard for the return-path round-tripped through /login: allow only internal,
 * single-slash paths. Blocks open redirects (`//evil.com`, `https://evil.com`),
 * control characters, and non-string junk coming from router `location.state`.
 */
export function toSafeInternalPath(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//')) return null; // protocol-relative → external
  if (hasControlChar(value)) return null;
  return value;
}
